import { Product, ProductSlug } from "@/api/product-api";
import Head from "next/head";
import { useHydrated } from "@/hooks/useHydrated";
import { useResettableState } from "@/hooks/useResettableState";
import { bundleTargetOf } from "@/libs/product/bundle-base";
import { CartUtil } from "@/utils/CartUtil";
import {
  camoProductOf,
  foldCamoBundleLines,
  resolveChosenBundle,
  resolveDisplayProduct,
} from "@/libs/product/bundle-preselect";
import { parseSetPack, SET_VARIANT_BY_SLUG } from "@/libs/product/set-variant";
import {
  SetOptionSelect,
  useSetDeal,
} from "@/components/tactsuit/components/buy-tactsuit/set-deal";
import { SizeLineSelect } from "@/components/tactsuit/components/buy-tactsuit/size-line-select";
import { sizeLineOf } from "@/libs/product/size-line";
import {
  GLOVE_SET_SIZE,
  resolvePreselectedGloveSet,
} from "@/libs/product/glove-set";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "@/libs/utils";
import { getServerSideProductProps } from "@/libs/getServerSideProductProps";
import { computeBadgeScrollTop } from "@/libs/pdp/badge-scroll";
import { getLocalePrefix } from "@/libs/i18n/getLocalePrefix";
import { LanCode } from "@/store/reducers/setting";
import { generateProductSeo, generateFaqJsonLd } from "@/config/seo";
import { createFeatureFlagService } from "@/libs/feature-flags/getFeatureFlags";
import { GLOBAL_E_FLAG_NAME } from "@/libs/locale/gl-country-param";
import { buildOfferUrl, resolveSeoOffer } from "@/libs/seo/product-offer";
import { getServerSideCountryCode } from "@/utils/LocaleUtil";
import { getServerFaqItems } from "@/libs/faq/getServerFaqItems";
import { ProductUtil } from "@/utils/ProductUtil";
import ProductFaq from "@/v2-components/pages/tactsuit/productFAQ";
import BuyTactsuitDetailPageTemplate, {
  useBuyActions,
} from "@/components/templates/BuyTactsuitDetailPageTemplate";
import { ProductOptionSelect } from "@/components/tactsuit/components/buy-tactsuit/ProductOptionSelect";
import {
  ProductGameKeys,
  useProductGameKeys,
  gameKeyAccordionValue,
} from "@/components/tactsuit/components/buy-tactsuit/ProductGameKeys";
import {
  RAIL_ACCORDION_ITEM,
  RAIL_SECTION_GAP,
  RAIL_ACCORDION_TRIGGER,
  RAIL_ACCORDION_TRIGGER_WITH_CAPTION,
  RAIL_ACCORDION_CONTENT,
  RailSectionHeader,
} from "@/components/tactsuit/components/buy-tactsuit/rail-accordion";
import { BadgesSelect } from "@/components/tactsuit/components/buy-tactsuit/BadgesSelect";
import { AccessoriesSelect } from "@/components/tactsuit/components/buy-tactsuit/AccessoriesSelect";
import { BundleUpgradeSelect } from "@/components/tactsuit/components/buy-tactsuit/BundleUpgradeSelect";
import { ProductFeatureSections } from "@/components/tactsuit/components/buy-tactsuit/ProductFeatureSections";
import {
  ProductInformationContent,
  hasProductInformation as checkProductInformation,
} from "@/components/tactsuit/components/buy-tactsuit/ProductInformationContent";
import { ProductImageGallery } from "@/components/tactsuit/components/buy-tactsuit/ProductImageGallery";
import {
  BUNDLE_DETAIL_RAIL,
  pdpFaqTagsForDisplayProduct,
  BundleFixedItemGroups,
  fixedGroupAccordionValue,
  GAMEKEY_DETAIL_ANCHOR,
  RailDetailsLink,
} from "@/components/tactsuit/components/buy-tactsuit/bundle-detail-rail";
import { useGetBundleProducts } from "@/services/product/useProductService";
import { useBuyTactSuit } from "@/store/useBuyTactSuit";
import TactSuitCard from "@/v2-components/TactSuitCard";
import PdpCheckoutButtons from "@/v2-components/pdp-checkout-buttons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/v2-components/ui/accordion";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import { GetServerSidePropsContext } from "next";
import { getAvailableColors, skuOptionHasColor } from "@/utils/product-colors";
import { useRouter } from "next/router";
import { ProductSections } from "@/components/tactsuit/components/ProductSections";
import { TrustBadges } from "@/components/tactsuit/components/buy-tactsuit/TrustBadges";
import { useGeRouted } from "@/hooks/useGeRouted";
import { ProductNaverPayButton } from "@/components/tactsuit/components/buy-tactsuit/ProductNaverPayButton";
import { isProd } from "@/Env";
import { isTradeInPageVisible } from "@/libs/trade-in/trade-in-display";
import TradeInProNotice, {
  TRADE_IN_TARGET_SLUG,
} from "@/v2-components/trade-in/trade-in-pro-notice";
import { Gap } from "@/components/tactsuit/Gap";
import { useTrans } from "@/utils/useTrans";
import { Button } from "@/v2-components/ui/button";
import RetailerSheet from "@/v2-components/pages/tactsuit/retailer-sheet";
import {
  shouldShowRetailerCta,
  shouldShowRetailerIcon,
} from "@/libs/product/retailer-cta";
import { RetailerIconButton } from "@/components/tactsuit/components/buy-tactsuit/retailer-icon-button";
import LocationLineIcon from "@/v2-components/core/icon/location-line-icon";
import { useRedirectWhenUnpriced } from "@/hooks/useRedirectWhenUnpriced";
import { usePricedFilter } from "@/hooks/usePricedFilter";

/**
 * ★**번들 → 베이스 매핑은 `@/libs/product/bundle-base` 로 옮겼다** (#5798).
 *
 * 여기 지역 상수로 두니 **목적지를 아는 곳이 이 페이지 하나**였고, 링크를 만드는 쪽은
 * 번들 slug 를 그대로 방출해 클릭마다 301 을 한 번 더 탔다. 지금은 `getProductDetailHref`
 * 와 아래 리다이렉트가 **같은 맵**을 본다. `camo-variant.ts` 가 #5386 에서 같은 이유로
 * 먼저 꺼내진 선례다.
 */

interface Props {
  product: Product;
  products: Product[];
  packageDeals: Product[];
  countryCode: string;
  color: string | null;
  /** 보상판매 진입로를 보이나 — 서버 시계 판정(`isTradeInPageVisible`, #6101). Pro 제품에서만 쓴다. */
  tradeInVisible: boolean;
}

export default function BuyTactsuitDetailPage({
  product,
  products,
  packageDeals,
  countryCode,
  color,
  tradeInVisible,
}: Props) {
  const router = useRouter();
  const locale = router.locale || "en";
  const { t } = useTrans(["shop", "tactsuit"]);
  // 이 국가·통화에서 가격이 없으면 제품 목록으로(판매 목록에서도 숨긴다) — 판정 조건은 훅 docblock.
  useRedirectWhenUnpriced(product);
  // 이 페이지의 판매 목록(액세서리·배지 선택 · Additional Accessories)용 — `usePricedFilter`.
  const pricedFilter = usePricedFilter();
  const {
    getSelectedBadgeAccessories,
    getSelectedNonBadgeAccessories,
    selectedProductOptions,
    setProduct,
    setOption,
    updateAccessory,
    setProducts,
    reset,
    getSku,
    selectedProduct,
  } = useBuyTactSuit((state) => ({
    selectedProduct: state.product,
    setProducts: state.setProducts,
    selectedProductOptions: state.options,
    setProduct: state.setProduct,
    reset: state.reset,
    getSku: state.getSku,
    setOption: state.setOption,
    getSelectedBadgeAccessories: state.getBadgeAccessories,
    getSelectedNonBadgeAccessories: state.getNonBadgeAccessories,
    updateAccessory: state.updateAccessory,
  }));

  // ── 크로스-제품 색상 (camo) ────────────────────────────────────────────────────
  // camo 는 SKU 옵션이 아니라 **별도 제품**(tactsuit-air ↔ tactsuit-air-camo)이지만
  // URL 은 기본 제품의 `?color=camo` 로 통합한다. `tactsuit-air-camo.tsx` 가 그 URL 로
  // 301 리다이렉트하고 있어 이미 확립된 규칙이다(소개 페이지 /tactsuit/tactsuit-air 도 동일).
  // 선택 시 가격·SKU·상세 콘텐츠가 전부 camo 제품 기준으로 바뀌어야 한다(issue #5068).
  // camo 는 색상 옵션이 아니라 **별도 제품**이다(서버 데이터: tactsuit-air 의 skuOptions 에
  // camo 가 없고 tactsuit-air-camo 가 따로 있다). 그래서 "라인(line)" 개념으로 다룬다:
  //   라인   = 색상 무관 제품군       (tactsuit-air ↔ tactsuit-air-camo, starter-bundle ↔ …-camo)
  //   라인 안에서 selectedColor 로 실제 제품을 고른다.
  // 이렇게 해야 번들을 고른 상태에서 색상을 바꿔도 **그 번들의 camo 변형**으로 가고,
  // 베이스 제품으로 떨어지지 않는다.
  // ⚠️ 이 지역 함수는 slug 가 아니라 **제품**을 돌려준다(공용 `camoVariantOf` 는 slug) — `camoProductOf`.
  const camoVariantOf = (slug: string) => camoProductOf(products, slug);

  const selectedColor =
    selectedProductOptions.find((o) => o.type === "color")?.value ?? color;
  const isCamo = selectedColor === "camo";

  const baseCamo = camoVariantOf(product.slug);
  // 기본 라인의 실효 제품 — 상세 콘텐츠/업그레이드 목록의 기준.
  const effectiveProduct = isCamo && baseCamo ? baseCamo : product;
  // 마운트 시 기본 색상 계산용(그 시점엔 번들 선택이 없으므로 기본 라인 기준).
  const baseColorSkuOptions = baseCamo
    ? [...product.skuOptions, ...baseCamo.skuOptions]
    : product.skuOptions;

  // 번들 업그레이드 인라인 선택 (기존 팝업 플로우 대체)
  const [upgradeChosen, setUpgradeChosen] = useState(false);
  // 수량 세트(MOLLE Pouch ×2) 선택 — 세트는 **별도 제품**이라 고르면 `displayProduct` 가 세트가 된다.
  // 매핑·근거는 `libs/product/set-variant.ts`. 마운트 이펙트에서 `upgradeChosen` 과 함께 리셋한다.
  //
  // ★기본은 **세트**다(#6008) — 사용자가 고르기 전(`null`)엔 거래가 성립하면(`useSetDeal`, 목록의
  // 세트 대표 카드 `useSetFirstBases` · 카트 묶기와 같은 술어) 세트, 아니면 단품. 성립하지 않으면
  // 선택 카드(`SetOptionSelect`)도 안 그려지므로 「고를 수 없는 세트가 골라진」 상태는 없다.
  //
  // 첫 선택은 주소의 `?pack=set|single` 이 정한다(R1 — 피드·구조화 데이터와 같은 규칙, `SET_PACK_QUERY`).
  // 거래가 성립하지 않으면 무엇을 골랐든 단품이다 — 고를 수 없는 세트를 골라진 채로 두지 않는다.
  const packQuery = parseSetPack(router.query.pack);
  // 같은 PDP 안에서 주소의 `?pack=` 만 바뀌면(카트의 세트 줄 → 같은 단품 PDP) 고른 값을 지우고 주소를
  // 다시 따른다. effect 로 지우면 낡은 선택으로 한 번 그려진다 — 렌더 중에 맞춘다(`useResettableState`).
  const [setChoice, setSetChoice] = useResettableState<boolean | null>(
    null,
    packQuery,
  );
  const hasSetVariant = !!SET_VARIANT_BY_SLUG[product.slug];
  const setDeal = useSetDeal(product.slug);
  const setChosen =
    hasSetVariant &&
    !!setDeal &&
    (setChoice ?? (packQuery ? packQuery === "set" : true));
  // 목록은 **기본 제품 기준으로 고정**한다. effectiveProduct 로 뽑으면 색상을 바꿀 때마다
  // 목록이 통째로 갈려서 선택 중이던 번들이 사라진다(실측: starter-bundle 선택 후 camo →
  // 베이스 TactSuit Air: Camo 로 떨어짐). camo 해석은 아래 라인 로직이 담당한다.
  const rawBundleUpgrades = useGetBundleProducts(product);
  // camo 변형은 **라인으로 접는다** — `Starter Bundle` 과 `Starter Bundle: Camo` 를 따로
  // 세우면 색상 스와치와 목록이 같은 선택을 두 번 묻는다. camo 는 색상으로 고른다.
  //
  // 이 중복은 슬리브 PDP 에서만 났다. Air PDP 는 `starter-bundle-camo` 가 air-camo SKU 를
  // 담아 `containsCurrentProduct` 에 안 걸려 애초에 1개였는데, 슬리브는 두 번들 모두
  // 슬리브 SKU 를 담아 2개로 갈렸다.
  //
  // 베이스가 목록에 없으면(카탈로그상 camo 만 잡히는 경우) 접지 않는다 — 접으면
  // `chosenBundle` 조회가 실패해 선택이 통째로 무효가 된다.
  const bundleUpgrades = useMemo(
    () => foldCamoBundleLines(rawBundleUpgrades),
    [rawBundleUpgrades],
  );
  // 목록에 **표시할** 제품은 현재 색상으로 해석한다. 라인 대표(starter-bundle)를 그대로
  // 그리면 camo 선택 중에도 비-camo 가격이 뜬다(실측: 레일 $490 vs 카드 $480).
  // 로직(라인 판정)은 위 `bundleUpgrades` 를 계속 쓰고, 여기는 렌더 전용이다.
  const bundleUpgradesForDisplay = useMemo(
    () =>
      bundleUpgrades.map((b) => {
        const camo = camoVariantOf(b.slug);
        return isCamo && camo ? camo : b;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bundleUpgrades, isCamo, products],
  );

  const canUpgrade =
    product.productType !== "BUNDLE" &&
    product.productType !== "ACCESSORY" &&
    bundleUpgrades.length > 0;

  // 글러브(DK2/DK3)면 선택 사이즈의 이너글러브 1개만 남는 필터가 안에 포함돼 있다.
  // ⚠️ develop #4921(DK3 정식 출시)에서 DK2 하드코딩 → isGloveDevice 로 일반화됐다.
  // 이 브랜치는 그 이전(5월) 코드라 DK2 만 검사했고, 그대로 두면 DK3 에서 전 사이즈
  // 이너글러브가 노출되는 회귀가 난다. develop 쪽 로직을 이식.
  //
  // **결제 경로(`addAccessoriesToCart`)와 같은 함수**를 쓴다 — 표시 목록과 청구 목록이
  // 갈리면 화면에 없는 물건이 결제된다(실측 2건, ProductUtil.getOfferedAccessories 주석).
  const { badgeAccessories, nonBadgeAccessories } =
    ProductUtil.getOfferedAccessories(
      // ★청구 허용 목록(`BuyTactsuitDetailPageTemplate` getOfferedAccessorySkus)과 같은 필터 — 표시 = 청구.
      pricedFilter(products),
      effectiveProduct.slug,
      selectedProductOptions,
    );

  /**
   * 옵션을 바꾸면 **더 이상 제공되지 않는 액세서리 선택을 해제**한다.
   *
   * ⚠️ 스토어를 깨끗하게 유지하는 게 목적이다. 스토어의 `accessories` 를 **직접 읽는
   * 결제 경로가 셋**이고(`addAccessoriesToCart` / `useCheckout.startCheckout` /
   * `buildAccessoryNaverPayItems`), 뒤 둘은 `selected` 만 보고 제공 여부는 안 본다.
   * 판정은 `ProductUtil.getOfferedAccessories` 하나로 하므로 목록과 어긋날 수 없다.
   *
   * 두 가지 축을 모두 본다(실측으로 확인한 사례):
   *   - **사이즈**: 이너글러브는 사이즈별 별도 제품 → S 선택 후 L 로 바꾸면 (S)가 목록에서 빠짐
   *   - **색상(camo)**: camo 는 **별도 제품**이라 `accessoryFor` 가 달라 목록이 통째로 바뀜
   *     (TactSuit Air → Camo 시 액세서리 섹션 자체가 사라진다)
   *
   * effect 가 아니라 **옵션 변경 핸들러**에서 처리한다 — 변경 시점에만 필요하고,
   * effect + setState 는 cascading render 경고를 늘린다.
   */
  const dropUnofferedAccessories = (nextOption: {
    type: string;
    value: string;
  }) => {
    // ⚠️ 빈 값은 **"아직 안 골랐다"이지 "제공되는 게 없다"가 아니다.**
    // 색상이 2개 이상인 글러브는 색상을 바꾸면 사이즈가 `""` 로 리셋된 채 이 핸들러로
    // 들어온다(TactGloveOptions.tsx 색상 RadioGroup → ProductOptionSelect 의 watcher,
    // 그쪽 주석에도 `changedValue === "" 일수 있다` 라고 적혀 있다).
    // 그대로 판정하면 `getTactGloveAccessaries` 가 `[]` 를 반환해 **정당한 이너글러브
    // 선택까지 해제되고 수량도 1로 리셋**된다. 같은 사이즈를 다시 골라도 체크가 풀린 채다.
    //
    // docs/pitfalls.md #29 의 "`?? []` 로 미상을 없음으로 뭉개지 말 것"과 같은 규칙이다.
    // (현재 다색 글러브는 DK2 뿐이고 그 PDP 는 API 가 제품을 안 내려줘 리다이렉트되지만,
    //  그건 코드 보장이 아니라 데이터 상태다 — 재게시되거나 새 다색 글러브가 나오면 바로 산다.)
    if (!nextOption.value) return;
    const nextOptions = [
      ...selectedProductOptions.filter((o) => o.type !== nextOption.type),
      nextOption,
    ];
    // 실효 제품도 **다음 옵션 기준**으로 다시 구한다 — 지금 렌더의 `effectiveProduct` 는
    // 아직 이전 색상 기준이라, camo 전환을 그걸로 판정하면 한 박자 늦는다.
    const nextColor = nextOptions.find((o) => o.type === "color")?.value;
    const nextEffectiveSlug =
      nextColor === "camo" && baseCamo ? baseCamo.slug : product.slug;
    const offered = ProductUtil.getOfferedAccessorySkus(
      products,
      nextEffectiveSlug,
      nextOptions,
    );
    // ⚠️ **badge 도 함께 본다.** `getOfferedAccessorySkus` 는 badge 를 포함해 집합을
    // 만드는데 여기서 non-badge 만 순회하면, 제공이 끊긴 badge 선택이 스토어에 남아
    // 결제 게이트에서 무음으로 빠지고 색상을 되돌리면 **클릭 없이 되살아나 청구**된다.
    // (non-badge 는 나가는 길에 해제되므로 두 클래스의 수명이 비대칭이 된다)
    [...getSelectedBadgeAccessories(), ...getSelectedNonBadgeAccessories()]
      .filter((a) => a.selected && !offered.has(a.sku))
      // 수량 리셋은 `Accessories.tsx` 의 수동 토글 규칙과 같다(해제 시 1로 되돌림).
      .forEach((a) => updateAccessory({ ...a, selected: false, quantity: 1 }));
  };

  useEffect(() => {
    reset();
    setUpgradeChosen(false);
    setSetChoice(null);
    setProducts(products);
    const initialColor =
      color ||
      getAvailableColors(baseColorSkuOptions).find((c) => c.available)?.color;
    if (initialColor) {
      setOption({ type: "color", value: initialColor });
    }
    // 글러브 세트 번들 주소(`tactglove-dk3-bundle`)는 이 PDP `?bundle=` 로 이관된다(`BUNDLE_BASE_BY_SLUG`).
    // 그때만 세트를 골라 둔 채 연다 — 판정은 구조화 데이터(`resolveSeoOffer`)와 같은 함수다.
    // ⚠️ 쿼리를 deps 에 넣지 않는다 — 칩을 누르면 `selectGloveSize` 가 `?bundle=` 을 shallow 로
    //    고치는데, deps 에 있으면 이 이펙트가 다시 돌아 `reset()` 이 액세서리·옵션 선택을 지운다.
    //    진입(제품·카탈로그가 바뀌는 내비게이션) 때만 주소를 읽는다.
    const bundleQuery =
      typeof router.query.bundle === "string" ? router.query.bundle : null;
    if (
      resolvePreselectedGloveSet({
        product,
        products,
        bundleSlug: bundleQuery,
        color: initialColor,
      })
    ) {
      setOption({ type: "size", value: GLOVE_SET_SIZE });
    }
  }, [product.slug, products, color]);

  // `?bundle=<slug>` 진입 힌트 (라인 판정에 쓰이므로 여기서 선언)
  const preselectBundleSlug =
    typeof router.query.bundle === "string" ? router.query.bundle : null;

  // 지금 사는 것 = 선택된 라인(번들 or 기본 제품)을 selectedColor 로 해석한 제품.
  // ⚠️ 지역 제한으로 그 번들이 목록에 없을 수 있다(일반 게임 키 번들은 KRW 에서 제외 · 번들 전용 키 번들은
  // 판매 제외 국가와 KRW 가격으로 — ProductUtil.getValidBundles, docs/bundle-region-policy.md). 그때 **기본 제품 선택이 그대로
  // 유지**된다 — 살 수 없는 번들을 선택된 것처럼 보여주지 않는다. 규칙은 `resolveChosenBundle` 한 곳 —
  // 서버 SEO(JSON-LD)와 GMC 피드가 같은 함수로 「화면이 고르는 번들」을 판정한다(#5858).
  const chosenBundle = resolveChosenBundle({
    bundleUpgrades,
    products,
    slug:
      upgradeChosen && selectedProduct
        ? selectedProduct.slug
        : preselectBundleSlug,
  });
  // 업그레이드 번들이 선택된 상태인가 — 헤더 캡션과 카드 선택 표시가 공유한다.
  const hasBundleSelection = upgradeChosen || !!chosenBundle;

  const lineBase = chosenBundle ?? product;
  const lineCamo = camoVariantOf(lineBase.slug);
  // 세트를 골랐으면 세트 제품이 「지금 사는 것」이다. 세트가 카탈로그에 없으면(판매중지 등)
  // 단품으로 떨어진다 — 그때는 `SetOptionSelect` 도 `useSetDeal` 이 null 이라 안 그려진다.
  const chosenSetProduct =
    setChosen && hasSetVariant
      ? (products.find((p) => p.slug === SET_VARIANT_BY_SLUG[product.slug]) ??
        null)
      : null;
  const displayProduct =
    chosenSetProduct ??
    resolveDisplayProduct({
      product,
      products,
      chosenBundle,
      isCamo,
    });
  const hydrated = useHydrated();

  // 색상 스와치는 라인의 두 제품 SKU 를 합쳐야 camo 가 노출된다.
  const optionSkuOptions = lineCamo
    ? [...lineBase.skuOptions, ...lineCamo.skuOptions]
    : lineBase.skuOptions;
  // 옵션 셀렉터 노출 조건: 선택 가능한 variant(색상 또는 사이즈)가 2개 이상.
  //
  // ⚠️ 색상 개수로만 판정하면 안 된다 — `ProductOptionSelect` 는 글러브에서 색상뿐 아니라
  // **사이즈·세트·사이즈 가이드·벌크 주문**까지 그린다. DK3 는 색상이 charcoal 하나뿐이라
  // 색상 기준 게이트(`canSelectColor`)에 걸려 사이즈 선택이 통째로 사라졌고, 사이즈를
  // 못 고르면 담을 수 없어 **구매가 막혔다.**
  //
  // develop #4921 이 이미 같은 버그를 SKU 개수 기준으로 고쳤는데, 3개월 드리프트
  // 머지(ece571dda, 충돌 9건)에서 이 파일을 브랜치 쪽으로 해소하며 그 수정을 잃었다.
  // develop 방식으로 되돌린다 — 글러브를 하드코딩하지 않아 멀티사이즈 제품이 새로
  // 생겨도 자동으로 커버된다.
  const canSelectOptions = optionSkuOptions.length > 1;
  // 스와치 **표시용** — 번들 전용 PDP 는 구성품 SKU 썸네일로 바꿔 보인다(`BUNDLE_DETAIL_RAIL[].colorSwatchSku`).
  // 선택 · 가격 · SKU 판정은 `optionSkuOptions` 그대로다.
  const swatchSku = BUNDLE_DETAIL_RAIL[product.slug]?.colorSwatchSku;
  const swatchThumbnail = swatchSku
    ? ProductUtil.getSkuThumbnail(products, swatchSku)
    : undefined;
  const swatchSkuOptions = swatchThumbnail
    ? optionSkuOptions.map((o) => ({ ...o, thumbnail: swatchThumbnail }))
    : optionSkuOptions;

  // 구성품(In the box)·FAQ 태그도 "지금 사는 것" 기준.
  let bundleComponents = ProductUtil.getBundleComponents(
    products,
    displayProduct,
  );
  if (bundleComponents.length === 0) {
    bundleComponents = [displayProduct];
  }

  const selectedSku =
    getSku() ||
    // 색상 판정은 `skuOptionHasColor` 하나로 통일한다 — 여기만 `sku` 문자열을
    // 대소문자 구분해 보고 있어서, 색상 칩은 뜨는데 이 조회만 빗나가면 갤러리가
    // 엉뚱한 이미지 세트로 폴백했다(`selectedSku` 가 "" 가 된다).
    (selectedColor
      ? optionSkuOptions.find((opt) => skuOptionHasColor(opt, selectedColor))
          ?.sku
      : undefined) ||
    "";

  const productImages = ProductUtil.getProductImages({
    product: displayProduct,
    sku: selectedSku,
    options: selectedProductOptions,
  });

  const isAccessory = effectiveProduct.productType === "ACCESSORY";
  const hasProductSections =
    (effectiveProduct.productSections?.length ?? 0) > 0;

  // Product Information 도 **화면에 보이는 그 제품**(displayProduct) 기준이다.
  //
  // ⚠️ 예전에는 `upgradeChosen && selectedProduct ? selectedProduct : effectiveProduct` 로
  // 따로 계산했는데, `displayProduct` 는 `?bundle=` 프리셀렉트(chosenBundle)까지 접는 반면
  // 이 식은 **클릭(upgradeChosen)만** 봤다. 그래서 `?bundle=starter-bundle` 로 진입해
  // 아무것도 클릭하지 않으면 레일·가격은 번들인데 Product Information 은 기본 제품을
  // 설명했다. 이 파일의 규칙("store 에 넣을 제품 = 화면에 보이는 그 제품")과도 어긋난다.

  // Product Information 에 실제로 보여줄 내용이 있는지 — 아코디언 노출 게이트.
  // 컴포넌트와 같은 함수를 써서 판정이 갈리지 않게 한다.
  const hasProductInformation = checkProductInformation(
    products,
    displayProduct,
    locale,
  );

  // 우측 옵션 아코디언 열림 상태 (하단 "배지 추가하러 가기" 버튼에서 제어)
  const [accordionValue, setAccordionValue] = useState<string[]>([]);

  // ── 기본 열림 섹션 (Upgrade · 게임 키) ─────────────────────────────────────
  // 둘 다 **선택이 필수**라 피그마에서 chevron_up(열림)으로 그려져 있다.
  //
  // ⚠️ `useState(canUpgrade ? ["upgrade"] : [])` 로는 안 된다 — `canUpgrade` 는
  // `useGetBundleProducts`(react-query)에서 오므로 **첫 렌더엔 항상 false** 다.
  // 초기값이 `[]` 로 굳어서 데이터가 도착해도 계속 닫힌 채였다(실측). 게임 키 목록도
  // 번들 선택에 따라 늦게 온다.
  // → "열린 값"을 state 에 넣지 않고 **닫은 값만 기억**해서 기본 열림을 파생시킨다.
  //   effect + setState 없이 같은 결과이고, 사용자가 접은 상태도 유지된다.
  const gameKeys = useProductGameKeys();
  const [closedSections, setClosedSections] = useState<string[]>([]);
  const defaultOpenValues = [
    ...(canUpgrade ? ["upgrade"] : []),
    ...gameKeys.map((g) => gameKeyAccordionValue(g.gameResell.id)),
    // 번들 전용 PDP 의 고정 구성품 그룹도 펼쳐 둔다(시안 `31635:39376` 은 세 그룹 모두 펼침 · 쉐브론 위)
    ...(BUNDLE_DETAIL_RAIL[displayProduct.slug]?.fixedGroups ?? []).map((g) =>
      fixedGroupAccordionValue(g.anchor),
    ),
  ];
  const railAccordionOpen = [
    ...accordionValue,
    ...defaultOpenValues.filter((v) => !closedSections.includes(v)),
  ];
  const onRailAccordionChange = (next: string[]) => {
    setAccordionValue(next.filter((v) => !defaultOpenValues.includes(v)));
    setClosedSections(defaultOpenValues.filter((v) => !next.includes(v)));
  };

  // store 에 넣을 제품 = 화면에 보이는 그 제품(displayProduct).
  // 가격·SKU·장바구니 담기가 전부 store product 기준이라 여기가 어긋나면 오청구가 된다.
  // 라인 모델이 색상까지 해석해 주므로 upgradeChosen 여부와 무관하게 항상 동기화해도
  // 사용자 선택을 덮어쓰지 않는다(선택은 resolveChosenBundle 로 이미 반영돼 있다).
  useEffect(() => {
    setProduct(displayProduct);
  }, [displayProduct, setProduct]);

  // 라우터가 반영을 끝내면 누적분을 버리고 다시 실제 URL 을 기준으로 삼는다
  // (뒤로/앞으로 가기로 URL 이 바뀐 경우 ref 가 stale 이 되지 않도록).
  const pendingSearchRef = useRef<string | null>(null);
  useEffect(() => {
    pendingSearchRef.current = null;
  }, [router.asPath]);

  /**
   * 쿼리 파라미터 1개를 URL 에 반영한다.
   *
   * ⚠️ `router.query` 가 아니라 **현재 URL** 을 기준으로 읽는다. shallow replace 는
   * 비동기라 색상·번들 동기화가 연달아 일어나면 뒤엣것이 앞엣것의 결과를 못 보고
   * 덮어쓴다(실측: camo 상태에서 Arizona 선택 → `?bundle=` 유실).
   *
   * replace + shallow 인 이유:
   *  - replace: 옵션을 여러 번 눌러도 히스토리가 쌓이지 않게 (선택은 뒤로가기 대상이 아님)
   *  - shallow: 목록·카탈로그는 이미 클라이언트에 있어 SSR 왕복 불필요
   */
  const replaceQueryParam = (key: string, value: string | null) => {
    // `router.replace` 는 비동기라 window.location 도 즉시 안 바뀐다. 한 인터랙션에서
    // 두 번 쓰면(번들 선택 → 색상 폴백) 뒤엣것이 앞엣것을 못 보고 덮어쓴다.
    // 직전에 "쓰기로 한" 쿼리를 ref 로 이어받아 누적한다.
    const base = pendingSearchRef.current ?? window.location.search;
    const params = new URLSearchParams(base);
    if (value === null) params.delete(key);
    else params.set(key, value);
    const qs = params.toString();
    pendingSearchRef.current = qs ? `?${qs}` : "";
    router.replace(
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
      undefined,
      { shallow: true, scroll: false },
    );
  };

  // 색상 선택을 URL 에 반영 — camo 는 별도 제품이라 색상 전환이 곧 제품 전환이고,
  // 링크 공유 시 재현돼야 한다. 기본 색상이면 파라미터를 제거해 URL 을 깔끔하게 둔다.
  const syncColorQuery = (nextColor: string) => {
    // 기본색 판정은 **현재 라인** 기준. 베이스 제품 기준으로 하면 색상이 없는 제품
    // (슬리브)에서 번들을 고를 때 기본색조차 `?color=` 로 붙어 URL 이 지저분해진다.
    const isDefault =
      nextColor ===
      getAvailableColors(optionSkuOptions).find((c) => c.available)?.color;
    replaceQueryParam("color", isDefault ? null : nextColor);
  };

  /**
   * 색상 선택 — **스토어 옵션 + URL 을 함께** 바꾼다.
   *
   * 유일한 호출처는 레일(`ProductOptionSelect` 의 `updateSelectedOption`)이다.
   * URL 만 바꾸면 `selectedColor` 가 안 움직여 스와치 선택 표시가 그대로 남고,
   * 스토어만 바꾸면 링크 공유·새로고침에서 재현되지 않는다. 그래서 **둘을 한 함수로
   * 묶어 둔다** — 레일 핸들러에서 두 줄을 인라인하면 다음 호출처가 한쪽만 부른다.
   *
   * ⚠️ `router.push(shallow:false)` 로 하면 안 된다 — `color` 는 GSSP prop 이라
   * full navigation 이 그 값을 바꾸고 마운트 이펙트가 `reset()` 을 태워
   * **배지·게임키·옵션 선택이 전부 지워진다.** (#5266 에서 본문 색상 스와치를 지울 때
   * 이 함수가 잠깐 고아가 됐는데, 지식이 여기에만 있어 지우지 않고 레일이 부르게 했다.)
   */
  const selectColor = (nextColor: string) => {
    setOption({ type: "color", value: nextColor });
    syncColorQuery(nextColor);
  };

  /**
   * 글러브 사이즈 선택 — 스토어 + **세트면 `?bundle=`** 을 한 짝으로 바꾼다(`selectColor` 와 같은 이유).
   *
   * 세트 번들 주소가 이 PDP `?bundle=` 로 이관되므로(`BUNDLE_BASE_BY_SLUG`) 세트 선택도 주소에 남겨야
   * 새로고침·링크 공유에서 재현된다(번들 · 색상과 같은 규칙). 반대로 단품 사이즈를 고르면 **지운다** —
   * 안 지우면 번들 주소로 들어와 S 를 고른 뒤 새로고침했을 때 세트가 다시 골라진다.
   * 단품 사이즈 자체는 주소에 싣지 않는다(일반 진입은 아무 사이즈도 미리 고르지 않는다).
   */
  const gloveSetSlug = CartUtil.getGloveBundleSlug(product.slug as ProductSlug);
  const selectGloveSize = (value: string) => {
    setOption({ type: "size", value });
    if (gloveSetSlug && bundleTargetOf(gloveSetSlug)?.base === product.slug) {
      replaceQueryParam(
        "bundle",
        value === GLOVE_SET_SIZE ? gloveSetSlug : null,
      );
    }
  };

  // 번들 선택을 URL 에 반영 — 링크 공유/새로고침 시 같은 선택이 재현된다.
  // "No upgrade"(= baseProduct 선택)면 파라미터를 제거한다. 안 지우면 URL 은 번들인데
  // 화면은 기본 제품이 되어 지금보다 나빠진다 (BundleUpgradeSelect 가 baseProduct 를 넘김).
  const syncBundleQuery = (picked: Product) => {
    const isBase = picked.slug === effectiveProduct.slug;
    replaceQueryParam("bundle", isBase ? null : picked.slug);
  };

  // ★ 현재 라인에 **유효한 색상이 항상 선택돼 있도록** 보장한다.
  // 색상이 안 잡히면 SKU 가 해석되지 않아 담기가 베이스 제품으로 떨어진다 —
  // 레일은 번들 가격을 보여주는데 장바구니엔 다른 제품이 들어간다(결제 무결성 문제).
  //
  // 두 경로에서 실제로 깨졌다:
  //  1. 색상 없는 제품 → 색상 있는 번들 선택. 예: 슬리브 PDP + `?bundle=starter-bundle`.
  //     마운트 시 기본 색상은 **베이스 제품**(슬리브, 색상 없음) 기준이라 아무것도 안 잡히고,
  //     번들 라인(ash/onyx/camo)으로 넘어가도 미선택으로 남았다.
  //     실측: 레일 Starter Bundle $480 → 담기면 TactSleeve $225.
  //  2. camo 상태에서 camo 변형이 없는 번들 선택. 예: Arizona(ash/onyx 만).
  //     색상 옵션은 camo 인데 스와치엔 camo 가 없어 미선택이 됐다.
  //
  // 둘 다 "선택된 색상이 현재 라인에 없다" 는 같은 조건이라 하나로 처리한다.
  useEffect(() => {
    const colors = getAvailableColors(optionSkuOptions);
    if (colors.length === 0) return; // 색상 개념이 없는 라인
    const isValid = colors.some(
      (c) => c.color === selectedColor && c.available,
    );
    if (isValid) return;
    const fallback = colors.find((c) => c.available)?.color;
    if (!fallback) return;
    setOption({ type: "color", value: fallback });
    syncColorQuery(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColor, optionSkuOptions]);

  // 클릭마다 증가. 아코디언 값(`["badges"]`)은 두 번째 클릭에서 같은 값이라 리렌더를
  // 안 만든다 — 그걸 트리거로 쓰면 **다시 눌렀을 때 스크롤이 안 된다**(사용자가 위로
  // 올라간 뒤 재클릭하는 경로). 눌린 횟수를 세면 매번 재실행된다.
  const [badgeFocusTick, setBadgeFocusTick] = useState(0);
  // 스크롤 목표. 같은 JSX 안에서 렌더하는 요소라 id 문자열 대신 ref 로 잡는다.
  const badgeAccordionRef = useRef<HTMLDivElement>(null);
  // ProductFeatureSections 의 React.memo 가 물게 하려면 참조가 고정돼야 한다.
  // 본문은 setter 만 닫으므로 deps 는 비어 있어도 안전하다.
  const openBadgeAccordion = useCallback(() => {
    // 배지 항목만 펼쳐 바로 보이게 한다. 스크롤은 아래 effect 가 **커밋 후** 처리한다 —
    // 여기서 바로 재면 펼침이 반영되기 전 레이아웃을 잰다.
    setAccordionValue(["badges"]);
    setBadgeFocusTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (badgeFocusTick === 0) return;

    // ⚠️ `getElementById("badge-accordion")` 이었는데 ref 로 바꿨다. 문자열 id 결합은
    // 누가 id 를 지우거나 이름을 바꿔도 **tsc·eslint·jest 가 아무것도 못 잡는다** —
    // 런타임에 조용히 `null` 이 되고 버튼만 죽는다. 아래 두 가림 요소는 다른 서브트리에
    // 있어 ref 를 끌어올 수 없어서 id 로 남기고, 대신 소스 가드 테스트로 잠갔다.
    const el = badgeAccordionRef.current;
    if (!el) return;

    // 상단을 가리는 것 = 스티키 헤더 스택 + 스크롤 시 내려오는 컴팩트 구매바.
    // 둘 다 브레이크포인트·배너 유무로 높이가 변하므로 상수 대신 실측한다
    // (CompactBuyBar 가 헤더 높이를 ResizeObserver 로 재는 것과 같은 이유).
    // ⚠️ 컴팩트 바는 배지 영역까지 스크롤하면 **반드시 보이는 상태**다(구매 CTA 를
    // 지나친 뒤라서). 지금 숨어 있어도 높이를 더해야 착지 후 트리거가 안 가린다.
    // 모바일에서는 `hidden laptop:block` 이라 높이가 0 으로 잡히는 게 정상이다.
    const occludedTop =
      (document.getElementById("buy-header-stack")?.getBoundingClientRect()
        .height ?? 0) +
      (document.getElementById("compact-buy-bar")?.getBoundingClientRect()
        .height ?? 0);
    const rect = el.getBoundingClientRect();
    // ⚠️ `null`(이미 보임) 과 `0`(페이지 최상단이 목표) 은 다르다 — falsy 검사 금지.
    const top = computeBadgeScrollTop({
      scrollY: window.scrollY,
      viewportHeight: window.innerHeight,
      rectTop: rect.top,
      rectBottom: rect.bottom,
      occludedTop,
    });
    if (top !== null) window.scrollTo({ top, behavior: "smooth" });
  }, [badgeFocusTick]);

  return (
    // camo 선택 시 구매 레일(제품명·가격·CTA)도 camo 제품 기준이어야 한다.
    // product 를 그대로 넘기면 히어로만 camo 로 바뀌고 가격은 기본가로 남아 오청구가 된다.
    <BuyTactsuitDetailPageTemplate
      product={effectiveProduct}
      countryCode={countryCode}
    >
      {/* 탭 제목을 **화면이 그리는 제품**으로 따라가게 한다(#5905). 서버 제목(`generateProductSeo`)은 진입
          주소 기준이라, 카드·색상을 눌러 `?bundle=` 이 shallow 로 바뀌면 처음 값이 남았다(실측: TactSleeve 에서
          Starter Bundle 카드 → 주소는 ?bundle=starter-bundle, 탭은 TactSleeve).
          ⚠️ **하이드레이션 뒤에만** 낸다 — SSR 시점의 화면 판정은 국가·flag·목록이 미확정이라 크롤러가 보는
          서버 제목을 틀린 값으로 덮을 수 있다. `next/head` 는 나중에 마운트된 `<title>` 을 남긴다. */}
      {hydrated && (
        <Head>
          <title>{`${displayProduct.name} | bHaptics`}</title>
        </Head>
      )}
      <CompactBuyBar />
      <div className="mt-6 px-4 laptop:mt-10">
        <Max>
          <div
            id="buy-section"
            className="flex flex-col gap-12 laptop:flex-row laptop:items-start laptop:gap-2"
          >
            {/* 좌측: 썸네일 열 + 라이트박스 캐러셀 — **데스크톱에서 고정**(#5270).
                Pro·Air 는 레일이 갤러리보다 460~475px 길어서 좌측에 큰 빈 공간이
                생겼다(실측: 갤러리 782 / 레일 Pro 1244 · Air 1256). 삼성·로지텍처럼
                이미지를 붙잡고 우측 패널만 스크롤시킨다.

                ⚠️ 종전에는 **레일 쪽**에 sticky 가 걸려 있었는데 Pro·Air 에서는
                레일이 컨테이너 전체 높이라 **sticky travel 이 0** — 고정이 아예 발동하지
                않았다(실측 0). 요청은 그 반대 방향이라 sticky 를 이쪽으로 옮겼다.

                ⚠️ 낮은 뷰포트에서 하단이 잘리는 것은 **수용한다.** 레퍼런스(삼성 buy
                페이지 `.preview_sticky`)도 `top:180px` + 미디어 575 를 고정해두고
                `max-height`·분기 없이 그대로 잘린다(실측: 뷰포트 650·560 에서 넘침).
                우리도 782 + top 96 = 878 이라 900 미만에서만 잘린다.

                ⚠️ **수용한 트레이드오프 — 고정 구간에서 마지막 썸네일이 덜 보인다.**
                썸네일 열은 6장 = 6×98 + 5×16 = 668 이다. 1280×720 실측:

                  scrollY 0        PR 전/후 동일 — 마지막 썸네일 화면 밖(177+668=845)
                  scrollY ~124     **PR 전**: 갤러리가 함께 올라가 열 전체가 보였다
                  scrollY 81~700   **PR 후**: top 96 고정 → 마지막 썸네일 43px 잘린 채 유지
                  scrollY 700+     컨테이너 끝에서 sticky 해제 → 완전히 보임

                즉 **기능 손실은 없지만**(96px 중 53px 노출 = 클릭 가능) **종전과 같지도
                않다** — 고정 구간 내내 조금 잘린 상태다. 클릭이 되므로 수용한다.
                ⚠️ 1366×768 은 763 vs 768 로 **여유가 5px 뿐**이다. `#buy-header-stack`
                높이는 `SuggestLocale`·`ReferralBanner`·`PromoBanner` 조합으로 변동하므로
                배너가 하나 붙으면 이 해상도도 잘리는 쪽으로 넘어간다(결론은 같다 —
                조금 더 잘릴 뿐 클릭 가능). 조치가 필요해지면 `top-24` 축소 또는 갤러리
                `max-h` + 내부 스크롤이 선택지다. */}
            <div className="min-w-0 flex-1 laptop:sticky laptop:top-24 laptop:self-start">
              {/* key 로 remount 하지 않는다 — next/image 6장이 통째로 재생성된다.
                  첫 장 복귀는 ProductImageGallery 내부가 이미지 목록 내용으로 처리한다. */}
              <ProductImageGallery
                images={productImages}
                productName={displayProduct.name}
              />
            </div>

            {/* 우측: 옵션/정보 아코디언 — **일반 흐름**(스크롤된다).
                고정은 좌측 갤러리가 맡는다(#5270). */}
            <div
              id="buy-rail"
              className={cn(
                "flex w-full flex-col",
                RAIL_SECTION_GAP,
                // 레일 박스 496 = 콘텐츠 464(피그마) + 좌우 패딩 16.
                // 패딩을 둔 이유는 **에러 배경**이다 — 미선택 시 섹션에 핑크가
                // 깔리는데 패딩이 없으면 글자에 딱 붙는다. 이 16px 안쪽까지
                // `RAIL_ERROR_HIGHLIGHT` 가 배경을 채워 여백을 만든다.
                // 갭 8 + 패딩 16 = 24 → **캐러셀과 레일 콘텐츠 사이는 종전 24px 그대로**.
                // 컨테이너 1440 기준: 좌측 936 + 갭 8 + 레일 496. 레일 콘텐츠 464 를
                // 지키는 대신 좌측 캐러셀이 952→936(라이트박스 830→814)으로 줄어든다
                // — 카드·헤더 수치가 전부 464 기준이라 그쪽을 고정했다.
                //
                // ⚠️ 여기 있던 `laptop:sticky laptop:top-24` 를 제거했다(#5270).
                // Pro·Air 에서는 레일이 컨테이너 전체 높이라 travel 0 으로 **작동한 적이
                // 없었고**, Visor·DK3 에서만 조금(78·12) 붙었다. 레일이 일반 흐름이 되면
                // 아래 두 가지가 함께 정리된다:
                //   · `buyCtaSentinelRef` 가 sticky 컨테이너 밖으로 나온다 — 그 주석이
                //     경고하던 "레일이 갤러리보다 짧아지면 트리거가 늦어진다" 가 해소
                //   · 배지 CTA 스크롤(`computeBadgeScrollTop`)의 "scrollY + rectTop 은
                //     문서 좌표" 전제가 pin 여부와 무관해진다
                "laptop:w-[496px] laptop:px-4",
                "laptop:shrink-0 laptop:self-start",
              )}
            >
              <BuyBox
                // 무료 원본 배지는 조끼 계열에만 들어간다. 아래 "Add Badges" 아코디언과
                // ProductFeatureSections 의 혜택 카드가 쓰는 기준을 그대로 쓴다.
                showFreeBadge={badgeAccessories.length > 0}
                tradeInNotice={
                  tradeInVisible && product.slug === TRADE_IN_TARGET_SLUG ? (
                    <TradeInProNotice />
                  ) : null
                }
                colorSelect={
                  canSelectOptions ? (
                    <ProductOptionSelect
                      key={displayProduct.slug}
                      productSlug={displayProduct.slug}
                      skuOptions={swatchSkuOptions}
                      selectedOptions={selectedProductOptions}
                      updateSelectedOption={(opt) => {
                        // 색상은 `selectColor` 가 스토어+URL 을 한 짝으로 처리한다.
                        // 예전엔 여기서 `setOption` + `syncColorQuery` 두 줄을 인라인
                        // 복제했는데, 그러면 `router.push` 금지 같은 함정 지식이 붙어
                        // 있는 쪽(`selectColor` docblock)과 실제 호출 경로가 갈린다.
                        if (opt.type === "color") selectColor(opt.value);
                        else if (opt.type === "size" && gloveSetSlug)
                          selectGloveSize(opt.value);
                        else setOption(opt);
                        dropUnofferedAccessories(opt);
                      }}
                    />
                  ) : hasSetVariant ? (
                    <SetOptionSelect
                      baseSlug={product.slug}
                      setChosen={setChosen}
                      onChange={setSetChoice}
                    />
                  ) : sizeLineOf(product.slug) ? (
                    // 사이즈별 별도 제품(Inner Gloves DK3 등) — 형제 사이즈 PDP 로 이동하는
                    // 선택지. 근거·동작은 `libs/product/size-line.ts` (#5149).
                    <SizeLineSelect
                      productSlug={product.slug}
                      products={products}
                    />
                  ) : undefined
                }
              />

              {hasProductSections && isAccessory && (
                <ProductSections
                  productSectionTitle={effectiveProduct.productSectionTitle}
                  productSections={effectiveProduct.productSections}
                  locale={locale}
                />
              )}

              <Accordion
                type="multiple"
                value={railAccordionOpen}
                onValueChange={onRailAccordionChange}
                className={cn("flex w-full flex-col", RAIL_SECTION_GAP)}
              >
                {canUpgrade && (
                  <AccordionItem
                    value="upgrade"
                    // ⚠️ 에러 배경(핑크)을 여기 걸면 안 된다. `validateSku` 가
                    // 검증하는 건 **게임 키·색상·사이즈뿐**이고 업그레이드는 대상이
                    // 아니다(안 골라도 장바구니에 담긴다 — 기본 제품으로 간주).
                    // 그런데 `showValidationErrors` 는 전역 플래그라, 다른 섹션 때문에
                    // 검증이 켜지면 업그레이드까지 엉뚱하게 빨개진다.
                    // 미선택 안내는 헤더의 빨간 보조줄로만 한다.
                    className={RAIL_ACCORDION_ITEM}
                  >
                    <AccordionTrigger
                      size="lg"
                      className={RAIL_ACCORDION_TRIGGER_WITH_CAPTION}
                    >
                      <RailSectionHeader
                        title={t("Upgrade your device with discount")}
                        // 헤더 캡션과 카드의 `hasSelected` 는 **같은 술어의 양면**이다.
                        // 예전엔 `!a && !b` 와 `a || !!b` 를 따로 적어서, 한쪽만 고치면
                        // 카드엔 선택 표시가 있는데 헤더는 "선택하세요" 로 남았다.
                        caption={
                          !hasBundleSelection && t("Please select an option")
                        }
                      />
                    </AccordionTrigger>
                    <AccordionContent className={RAIL_ACCORDION_CONTENT}>
                      <BundleUpgradeSelect
                        bundles={bundleUpgradesForDisplay}
                        // base/selected 도 색상 해석된 제품으로 맞춘다 — 카드가 camo
                        // 변형으로 그려지는데 비교 대상만 라인 대표면 선택 표시가 어긋난다.
                        baseProduct={effectiveProduct}
                        selectedProduct={displayProduct}
                        // 사전선택(?bundle=)도 '선택됨'으로 표시 — 안 그러면 스토어엔
                        // 번들이 들어갔는데 UI 는 "Please select an option" 으로 남는다.
                        hasSelected={hasBundleSelection}
                        onSelect={(p) => {
                          setProduct(p);
                          setUpgradeChosen(true);
                          syncBundleQuery(p);
                        }}
                      />
                    </AccordionContent>
                  </AccordionItem>
                )}

                {/* ⚠️ 게임 키는 **Upgrade 다음**이다(피그마 레일 y: Upgrade 890 →
                    Game Key 1534). 어떤 번들을 고르느냐로 받을 키가 정해지므로
                    순서가 뒤집히면 안 된다. */}
                <ProductGameKeys
                  detailsLink={
                    BUNDLE_DETAIL_RAIL[displayProduct.slug] && (
                      <RailDetailsLink
                        label={t(
                          BUNDLE_DETAIL_RAIL[displayProduct.slug]!
                            .gameKeyLinkKey,
                        )}
                        anchor={GAMEKEY_DETAIL_ANCHOR}
                      />
                    )
                  }
                />
                {/* 번들 전용 PDP 의 고정 구성품(EXOSHOCK 배지 · 메시 라이닝) — 게임 키 다음(시안 레일 순서) */}
                <BundleFixedItemGroups slug={displayProduct.slug} />

                {badgeAccessories.length > 0 && (
                  <AccordionItem
                    value="badges"
                    // 혜택 카드의 "Add More Badges" 가 여기로 스크롤한다(#5264).
                    ref={badgeAccordionRef}
                    className={RAIL_ACCORDION_ITEM}
                  >
                    <AccordionTrigger
                      size="lg"
                      className={RAIL_ACCORDION_TRIGGER}
                    >
                      {t("Add Badges")}
                      <ChevronDownIcon className="size-6 shrink-0" />
                    </AccordionTrigger>
                    <AccordionContent className={RAIL_ACCORDION_CONTENT}>
                      <BadgesSelect
                        accessories={badgeAccessories}
                        selectedAccessories={getSelectedBadgeAccessories()}
                        onChange={updateAccessory}
                      />
                    </AccordionContent>
                  </AccordionItem>
                )}
                {nonBadgeAccessories.length > 0 && (
                  <AccordionItem
                    value="accessories"
                    className={RAIL_ACCORDION_ITEM}
                  >
                    <AccordionTrigger
                      size="lg"
                      className={RAIL_ACCORDION_TRIGGER}
                    >
                      {t("Add Accessories")}
                      <ChevronDownIcon className="size-6 shrink-0" />
                    </AccordionTrigger>
                    <AccordionContent className={RAIL_ACCORDION_CONTENT}>
                      <AccessoriesSelect
                        product={displayProduct}
                        accessories={nonBadgeAccessories}
                        selectedAccessories={getSelectedNonBadgeAccessories()}
                        onChange={updateAccessory}
                      />
                    </AccordionContent>
                  </AccordionItem>
                )}
                {/* mgmt 에 productSections 미등록 제품(예: TactVisor, Tactosy for
                    Feet)은 열어도 빈 아코디언이라 트리거 자체를 숨긴다.
                    등록되면 자동으로 다시 나타난다.
                    판정은 `hasProductInformation`(ProductInformationContent) — 내용 렌더와 같은 표 · 같은 함수다. */}
                {hasProductInformation && !isAccessory && (
                  <AccordionItem
                    value="product-information"
                    className={RAIL_ACCORDION_ITEM}
                  >
                    <AccordionTrigger
                      size="lg"
                      className={RAIL_ACCORDION_TRIGGER}
                    >
                      {t("Product Information", { ns: "tactsuit" })}
                      <ChevronDownIcon className="size-6 shrink-0" />
                    </AccordionTrigger>
                    <AccordionContent className={RAIL_ACCORDION_CONTENT}>
                      <ProductInformationContent
                        products={products}
                        displayProduct={displayProduct}
                        locale={locale}
                      />
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </div>
          </div>
        </Max>
      </div>
      {/* 제품 소개 페이지 콘텐츠 (로지텍 Key features 스타일로 구매 영역 아래 노출) */}
      {/* camo 선택 시 상세 콘텐츠도 camo 구성으로 전환 (issue #5068) → effectiveProduct */}
      <ProductFeatureSections
        product={effectiveProduct}
        products={products}
        packageDeals={pricedFilter(packageDeals)}
        onAddBadge={
          badgeAccessories.length > 0 ? openBadgeAccordion : undefined
        }
        // ⚠️ 색상은 안 내려보낸다(#5266). 본문의 "Details" 블록(색상 스와치 + 색상별
        // 이미지 캐러셀)을 지웠고, 색상 선택은 레일의 `ProductOptionSelect`, 색상별
        // 이미지는 상단 갤러리가 `?color=` 로 담당한다.
      />
      {/* 구성품·추가 악세사리는 ProductFeatureSections 내 TactsuitTechSpecs가 담당 (기획 순서) */}
      <Gap className="px-4">
        <Max className="max-w-screen-mdl">
          <ProductFaq
            className="max-w-full !px-0"
            tag={pdpFaqTagsForDisplayProduct(products, displayProduct)}
          />
        </Max>
      </Gap>
      <Gap />
    </BuyTactsuitDetailPageTemplate>
  );
}

/**
 * 로지텍 PDP 스타일 buy box — 제품명/가격/옵션/재고/CTA를 우측 레일 상단에 배치
 */
const BuyBox = ({
  colorSelect,
  showFreeBadge,
  tradeInNotice,
}: {
  colorSelect?: React.ReactNode;
  /** 보상판매 진입로(#6101) — Pro 제품 · 공개 이후에만 페이지가 채운다. 신뢰 배지 아래에 둔다. */
  tradeInNotice: React.ReactNode;
  // ⚠️ 필수다. `TrustBadges` 로 그대로 흘러가는데, 옵셔널이면 여기서 `undefined` 가
  // 되어 "빠뜨리면 조용히 사라지는" 경로가 되살아난다(`/cart` 가 그랬다).
  showFreeBadge: boolean;
}) => {
  const {
    onAddToCart,
    onCheckout,
    onAccountCheckout,
    isCheckingOut,
    isSoldOut,
    displayProduct,
    checkoutProduct,
    offeredAccessorySkus,
    buyCtaSentinelRef,
  } = useBuyActions();
  const getSku = useBuyTactSuit((s) => s.getSku);
  const sku = getSku() ?? undefined;
  // GE 국제 체크아웃 대상인가 — 예상 출고일 숨김(#5829). 사유는 그 렌더 자리 주석.
  const geRouted = useGeRouted();
  // `Find a retailer` 는 `RetailerSheet` 기본 trigger 와 같은 키를 쓴다(tactsuit ns).
  const { t } = useTrans(["tactsuit"]);
  // 번들 전용 PDP 의 상품 요약 · 신뢰 배지(EXOSHOCK — `BUNDLE_DETAIL_RAIL`). 없으면 종전 그대로
  const rail = BUNDLE_DETAIL_RAIL[displayProduct.slug];

  return (
    // ⚠️ 값 표시는 `checkoutProduct`(선택 SKU 소유 제품). `displayProduct` 로 두면
    // 세트 선택 시 레일은 단품 가격, 카트엔 세트가 담긴다.
    <TactSuitCard product={checkoutProduct} sku={sku}>
      {/* 섹션 내부 간격 — 모바일 20(28718:163612), 데스크톱 24 */}
      <div className="flex flex-col gap-5 laptop:gap-6">
        {/* 헤더 블록 간격 — 모바일 8(28718:163613), 데스크톱 16 */}
        <div className="flex flex-col gap-2 laptop:gap-4">
          {/* 제품명 — 모바일 22/28(28718:163614), 데스크톱 32/40. 둘 다 w400.
              `text-2xl` = 22/28, `text-headline-lg` = 32/40 (커스텀 스케일). */}
          {rail?.summary && (
            <p className="text-sm tracking-[0.4px] text-red-60">
              {rail.summary.eyebrow}
            </p>
          )}
          <TactSuitCard.Name className="text-2xl laptop:text-headline-lg" />
          {rail?.summary && (
            <p className="text-sm tracking-[0.4px] text-coolgray-90">
              {`${rail.summary.subtitle} · ${t(rail.summary.subtitleKey)}`}
            </p>
          )}
          {/* 가격 — 모바일 16/22 w600 ls.5(28718:163710), 데스크톱 24/32 w400.
              ⚠️ 모바일은 **semibold** 다. 데스크톱만 `font-400` 이 붙는다. */}
          <TactSuitCard.PriceDetail
            className="justify-start gap-2 text-lg tracking-[0.5px] laptop:text-3xl laptop:tracking-normal"
            salePriceClassName="font-600 text-blue-60 laptop:font-400"
          />
          {/* 배송예정일 + 저재고 경고(5개 미만, 기존 로직 유지).
              아이콘은 EstShippingDate 의 `icon` prop 으로 교체한다 — 앞에 따로 두면
              기본 트럭 아이콘과 겹쳐 2개가 된다. 피그마는 location-line 24px. */}
          {/* Extra 블록 간격 — 모바일 4(28718:163714), 데스크톱 8 */}
          <div className="flex flex-col justify-center gap-1 laptop:gap-2">
            {/* ★GE 국제 체크아웃 대상이면 **그리지 않는다** (#5829 · GE 요청 — 「PDP 예상 배송일」).
                이 날짜는 도착일이 아니라 **출고 예정일**이다(2026-09-29 en 문구를 「Est. Ship
                Date」로 명확히 함 · 나머지 6 locale 은 원래 발송/출고 뜻). 유지해도 되는지 GE
                답이 없어 **요청대로 숨긴다** — 유지로 답이 오면 이 조건만 걷는다.
                판정은 카트와 같은 `useGeRouted()`. GE 숨김 CSS 를 안 쓰는 이유는 `TrustBadges`
                의 `geRouted` 주석(자리가 남는다). `EstShippingDate` 는 범용 카드 조각이라 GE
                판단을 안에 넣지 않고 여기서 한다 — 이 페이지가 유일한 소비처다. */}
            {!geRouted && (
              <TactSuitCard.EstShippingDate
                className="text-sm tracking-[0.4px]"
                icon={<LocationLineIcon className="size-6 shrink-0" />}
              />
            )}
            <TactSuitCard.Stock className="text-sm tracking-[0.4px]" />
          </div>
        </div>
        {/* ★버튼 — 시안 개정(29116:30958 비로그인 / 29121:31081 로그인).
            `Payments` 블록 세로 gap 16(데스크톱) · 12(모바일).
            종전에는 `Add to Bag` 이 **전체폭 별도 행**(데스크톱 h54/16px)이었다. 개정판은
            전부 **한 행 h40 / 글자 12** 다. */}
        {/* ★sticky CTA 노출 기준점 = **이 컬럼 자체**다. 높이 0 sentinel 을 마지막 자식으로
            두지 않는다 — `flex` 의 `gap` 은 **자식의 높이와 무관하게** 붙으므로, 높이 0
            요소도 자기 앞의 gap 을 그대로 먹는다. 실측(1440): 콘텐츠 끝 369 → Benefits 409
            로 **보이는 갭이 40** 이었다(컬럼 gap 16 + 바깥 gap 24). 시안은 24 다.

            트리거 지점은 그대로다. `useScrollPast` 는 "요소가 화면 위로 **완전히** 빠져나갔나"
            (`!isIntersecting && top < 0`)를 보므로, 컬럼을 관측하면 **컬럼의 아래끝**이 기준이
            된다 — 마지막 자식이던 sentinel 의 위치와 같다. DOM 노드도 하나 줄어든다.

            ⚠️ 관측 대상은 **CTA 컬럼**이어야 한다. 구매 섹션 전체(`#buy-section`)를 관측하면
            아코디언을 열수록 공백이 벌어진다(컨텍스트 타입 주석 참고).
            ⚠️ 레일에 `laptop:sticky` 를 다시 넣으면 "레일이 고정되면 기준점도 같이 고정돼
            트리거가 늦어진다" 는 제약이 되살아난다(#5270 에서 sticky 를 갤러리로 옮겨 해소). */}
        <div
          ref={buyCtaSentinelRef}
          className="flex flex-col gap-3 laptop:gap-4"
        >
          {/* ★행 구조는 시안 29116:30970 그대로 **2단 중첩**이다.
              `Row`(gap 20) = [ 40px 아이콘 ] + `Btns`(flex-1, gap 12) = [ 담기 ][ 결제 ]

              ⚠️ 셋을 형제로 두면 gap 이 하나뿐이라 20/12 를 동시에 못 준다. 종전엔 그
              하나를 `isSplitCheckout`(로그인 여부)에 묶어 뒀는데, 아이콘이 리테일러로
              바뀐 뒤로 **로그인 여부와 무관**해졌다 — 로그아웃에서 버튼 사이가 20 으로
              벌어져 어긋났다(실측). 모바일은 둘 다 8. */}
          <div className="flex w-full items-center gap-2 laptop:gap-5">
            {/* 재고 있을 때의 리테일러 = **40px 원형 아이콘** (시안 맨 왼쪽).
                품절 버전(아래 `Btns` 안)과 대상은 같고 모양만 다르다.

                ⚠️ **단종은 제외**된다(술어 안에서). 단종 제품은 리테일러에도 재고가 없어
                헛걸음이고, 판정 축이 재고와 다르다 — 유일한 단종 제품 `tactosy-for-feet`
                이 `retailers` 3곳을 갖고 있어서 데이터만 보면 떠 버린다. */}
            {shouldShowRetailerIcon(displayProduct) && (
              <RetailerSheet
                retailers={displayProduct.retailers}
                trigger={<RetailerIconButton />}
              />
            )}
            {/* ⚠️ **그리드다.** flex + `flex-1` 로는 균등이 안 나온다 — 실측(1440): 담기 220 /
                  결제 172 로 갈렸고 `min-w-0` 을 줘도, 인라인으로 `flex:1 1 0` 을 강제해도
                  그대로였다. `auto-cols-fr` 은 내용과 무관하게 칸을 똑같이 나눈다.
                  같은 문제를 `PdpCheckoutButtons` 가 이미 그리드로 풀어 뒀다(#5283). */}
            <div className="grid min-w-0 flex-1 auto-cols-fr grid-flow-col items-center gap-2 laptop:gap-3">
              {/* 품절이면 `Find a Retailer` 가 **Notify Me 옆에** 반반으로 들어간다
                  (시안 29444:145342). 이때는 위 아이콘이 없다. */}
              {shouldShowRetailerCta(displayProduct) && (
                <RetailerSheet
                  retailers={displayProduct.retailers}
                  trigger={
                    <Button
                      variant="blue-outline"
                      shape="pill"
                      size="sm"
                      className="h-10 min-w-0 flex-1 px-6"
                    >
                      {t("Find a retailer")}
                    </Button>
                  }
                />
              )}
              <TactSuitCard.NotifyOrAddToBag
                // ⚠️ `iconOnly` 를 **주지 않는다.** 결제가 둘로 갈리던 동안엔 한 행에 버튼이
                //    3개가 되어 `Add to Bag` 이 아이콘으로 줄었지만(#5317), 이제 결제는
                //    하나이고 아이콘 자리는 **리테일러**가 쓴다 — 담기는 항상 라벨이다.
                //    품절이면 이 자리에 `Notify me` 가 그려진다(컴포넌트가 판단).
                // ⚠️ `min-w-0` 이 없으면 균등 분배가 깨진다. flex 항목의 기본
                //    `min-width: auto` 는 **min-content 아래로 안 줄어들어**, 라벨이 긴 쪽이
                //    자기 폭을 먼저 가져간다(실측 1440: 담기 220 / 결제 172). 시안 참조
                //    코드에도 `min-w-px` 가 붙어 있다.
                className="h-10 min-w-0 flex-1"
                disabled={isCheckingOut}
                addToCart={onAddToCart}
              />
              {!isSoldOut && (
                // develop #5101 — 비회원/회원 결제 분리. 로그인 상태·인증 로딩 중에는
                // 컴포넌트가 알아서 버튼 하나로 그린다(기존 동작 유지).
                <PdpCheckoutButtons
                  // `min-w-0` — 위 담기 버튼과 같은 이유(균등 분배).
                  className="h-10 min-w-0 flex-1"
                  isCheckingOut={isCheckingOut}
                  onGuestCheckout={onCheckout}
                  onAccountCheckout={onAccountCheckout}
                />
              )}
            </div>
          </div>
          {/* ★ 메인 레일(본문)의 네이버페이. #5081 리뉴얼 때 이 자리가 비어 있었고
              살아있는 사본은 laptop:hidden 안이라 **데스크톱 KR 사용자에게 네이버페이가
              아예 안 보였다.** 컴포넌트가 flag/품절/KRW 가격 게이트를 스스로 판단한다. */}
          {/* ★★**모바일에서도 여기는 유지한다** — 반응형 게이트를 붙이지 말 것
              (기획 확인 2026-08-26, #5378).

              #5378 이 지운 것은 **모바일 스티키 하단 바**의 사본뿐이다. 시안
              `버튼정책`(29444:145342)의 모바일 3케이스는 스티키 바 프레임이고, 그 `Payments`
              가 버튼 2개뿐이다 — **모바일 인라인 프레임은 시안에 없다.** 그래서 "모바일에서는
              네이버페이를 숨긴다" 로 읽으면 안 된다.

              여기에 `laptop:` 류 게이트를 붙이면 **모바일 KR 사용자가 네이버페이로 결제할 수
              없다.** #5081 이 데스크톱에서 그 상태였고, 그 반대 방향이 된다.

              ⚠️ **이 불변식은 오늘 CI 에서 잠글 층이 없다** — 비용 문제가 아니라 층의 한계다:
                · jsdom 은 Tailwind 를 평가하지 않는다 → `hidden laptop:block` 이 클래스
                  문자열로만 남아 「모바일에서 안 보인다」를 assert 할 수 없다(PDP 를 통째로
                  세워도 마찬가지다)
                · Playwright 로는 가능하지만 **CI 에 그 잡이 없다** — `test.yml` 은
                  `legals:check` · `legals:pdfs --check` · `typecheck` · jest 만 돌리고,
                  `tests/` 9개 스펙에 PDP·네이버페이 커버리지가 0이다
                · 게이트는 조상 `<div>` 에도 붙을 수 있어 소스 슬라이스로는 못 본다(JSX AST 필요)
              그래서 방어는 **이 주석 + 수동 QA** 다. 「비싸서 안 했다」로 읽지 말 것 —
              싸게 만들려는 시도로 하루를 쓰게 된다(리뷰 #5379 Nit 2). 후속: #5380 */}
          {/* ⚠️ `checkoutProduct` 여야 한다 — 이 버튼은 product 에서
              basePrice·slug·name 을 뽑고 sku 는 `getSku()` 에서 가져온다.
              `displayProduct` 를 넘기면 세트 선택 시 **한 payload 안에서 앞뒤가
              다른 제품**을 가리켜, 재고 검증이 0 을 받아 구매가 막힌다. */}
          {/* 액세서리 집합만은 **실효 제품** 기준이다 — `checkoutProduct` 는 번들일 수
              있고, 그러면 `accessoryFor` 가 안 걸려 집합이 비어 정당한 액세서리까지
              주문에서 빠진다. 화면 목록과 같은 기준을 그대로 넘긴다. */}
          <ProductNaverPayButton
            product={checkoutProduct}
            isSoldOut={isSoldOut}
            offeredAccessorySkus={offeredAccessorySkus}
          />
        </div>
        <TrustBadges
          showFreeBadge={showFreeBadge}
          items={rail?.trustBadges?.map((b) => ({
            icon: b.icon,
            label: t(b.labelKey),
          }))}
        />
        {tradeInNotice}
        {/* 옵션(색상/사이즈)은 Benefits 박스 **아래** 48px (피그마 28715:139500 — 레일의
            모든 섹션이 48px 간격, 첫 섹션 내부만 24px). 바깥 flex gap-6(24) + mt-6(24). */}
        {!!colorSelect && <div className="mt-6">{colorSelect}</div>}
      </div>
    </TactSuitCard>
  );
};

/**
 * 하단 설명 영역으로 스크롤하면 상단에 나타나는 컴팩트 구매 바 (로지텍 스타일)
 */
/**
 * 구매 섹션을 지나면 슬라이드인하는 데스크톱 상단 바.
 * 노출 신호는 **컨텍스트**에서 받는다 — 모바일 하단 sticky 와 같은 값을 써야
 * 두 바가 다른 지점에서 뜨지 않는다(`BuyActions.buyCtaSentinelRef` 주석 참고).
 */
const CompactBuyBar = () => {
  const {
    onAddToCart,
    onCheckout,
    onAccountCheckout,
    isCheckingOut,
    isSoldOut,
    checkoutProduct,
    scrolledPastBuyCta: visible,
  } = useBuyActions();
  const getSku = useBuyTactSuit((s) => s.getSku);
  const sku = getSku() ?? undefined;

  // 헤더 스택(sticky top-0 z-30) 바로 아래에 붙인다.
  // 예전엔 이 바도 `top-0 z-40` + 불투명 배경이라 데스크톱에서 스크롤 시 로고·메뉴·카트
  // 아이콘을 통째로 가렸다. 헤더 높이는 배너 유무로 달라지므로 상수 대신 실측한다.
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    const el = document.getElementById("buy-header-stack");
    if (!el) return;
    const update = () => setHeaderHeight(el.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      // 배지 CTA 의 스크롤 계산이 이 바 높이를 실측해서 가림 높이에 더한다(#5264).
      id="compact-buy-bar"
      style={{
        top: headerHeight,
        // ⚠️ 숨김은 **화면 밖으로** 보낸다. 예전엔 `-translate-y-full`(자기 높이 65 만큼)
        // 이었는데 `top: headerHeight`(≈70) 이 붙어 있어 숨김 위치가 top≈5 —
        // 화면 밖이 아니라 **헤더 뒤에 겹쳐 있었다**(z-20 < 헤더 z-30).
        // 그래서 오버스크롤(러버밴딩)이나 헤더 높이 변화 순간에 구매 버튼이 비쳤다.
        // `-100%`(자기 높이) + `headerHeight`(내려온 만큼)을 함께 올려야 완전히 나간다.
        transform: visible
          ? "translateY(0)"
          : `translateY(calc(-100% - ${headerHeight}px))`,
      }}
      // ⚠️ 숨김 중엔 **포커스도 막는다.** 안 막으면 Tab 이동 시 화면 밖 버튼에 포커스가
      // 가고, 브라우저가 그걸 보이게 스크롤하면서 숨긴 바가 그대로 드러난다
      // (키보드 사용자에겐 사실상 포커스 트랩). React 19 의 boolean `inert`.
      inert={!visible}
      // z 는 헤더(z-30)보다 낮게 — 위치로도 안 겹치지만, 헤더가 항상 위에 오도록 보장한다.
      className="fixed inset-x-0 z-20 hidden border-b border-coolgray-20 bg-white shadow-sm transition-transform duration-300 laptop:block"
    >
      <div className="mx-auto flex w-full max-w-screen-max items-center justify-between px-4 py-3">
        <TactSuitCard product={checkoutProduct} sku={sku}>
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-4">
              <TactSuitCard.Name className="text-lg font-600" />
              <TactSuitCard.PriceDetail className="justify-start text-base" />
            </div>
            <div className="flex items-center gap-2">
              <TactSuitCard.NotifyOrAddToBag
                disabled={isCheckingOut}
                addToCart={onAddToCart}
              />
              {!isSoldOut && (
                <PdpCheckoutButtons
                  isCheckingOut={isCheckingOut}
                  onGuestCheckout={onCheckout}
                  onAccountCheckout={onAccountCheckout}
                />
              )}
            </div>
          </div>
        </TactSuitCard>
      </div>
    </div>
  );
};

const Max = ({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) => {
  return (
    <div className={cn("mx-auto w-full max-w-screen-max", className)}>
      {children}
    </div>
  );
};

export async function getServerSideProps(
  context: GetServerSidePropsContext<{ slug: ProductSlug }>,
) {
  const { slug } = context?.params ?? {};
  const locale = (context?.locale ?? "en") as LanCode;

  // 번들 PDP → 베이스 제품 + `?bundle=` 로 이관. 번들 선택은 구매 레일 인라인이 담당한다.
  // ⚠️ 국가/통화로 분기하지 않는다 — 번들 유효성(일반 게임 키 KRW 제외 등)은 클라이언트가 게임 키 재고와
  //    currency 로 판정하는데(useGetBundleProducts), 서버는 게임 키 재고를 조회하지 않아 같은 판정을
  //    할 수 없다(`getBundleUpgradeCandidates` 에 `gameResells: []`). 항상 베이스+파라미터로 보내고 선택
  //    여부는 클라이언트가 정한다. 번들이 그 지역에서 무효면 사전선택이 무시되고 기본 제품 선택이 유지된다.
  //    (이유는 CDN 캐시가 아니다 — 이 페이지는 원래 국가별 카탈로그를 받고 캐시 헤더가 없다, #5905 R1)
  const bundleBase = slug ? bundleTargetOf(slug) : undefined;
  if (bundleBase) {
    const localePrefix = getLocalePrefix(context.locale);
    return {
      redirect: {
        // ⚠️ **베이스 뒤에 `/` 를 붙인다** (#5798). `trailingSlash: true` 라 빠지면 308 이
        //    한 번 더 난다 — prod 실측(2026-09-18)에서 번들이 **홉 2**인 이유의 절반이
        //    이것이었다(나머지 절반은 링크가 번들 slug 를 그대로 가리킨 것).
        destination: `${localePrefix}/shop/buy-tactsuit/${bundleBase.base}/?${bundleBase.query}`,
        permanent: true,
      },
    };
  }

  const props = await getServerSideProductProps(slug ?? null)(context);

  if (!props.props?.product) {
    return { redirect: { destination: "/tactsuit/", permanent: false } };
  }

  const product = props.props.product;

  // 상품 데이터에서 SEO 생성 — 제목·구조화 데이터는 **화면이 그리는 제품**과 **그 국가의 판매가**다(#5858).
  // GMC 는 랜딩 페이지 구조화 데이터로 피드 가격을 검증한다. 국가는 쿠키(→ IP), flag 는 서버에서 확정 ON 만.
  // ⚠️ 국가에 따라 응답이 달라지므로 이 페이지에 CDN 캐시를 걸지 말 것(docs/locale-middleware-policy.md §1.4).
  const bundleSlug =
    typeof context.query.bundle === "string" ? context.query.bundle : null;
  const colorQuery =
    typeof context.query.color === "string" ? context.query.color : null;
  const flags = await createFeatureFlagService().getFlags();
  const pack = parseSetPack(context.query.pack);
  const offer = resolveSeoOffer({
    product,
    products: props.props.products,
    countryCode: getServerSideCountryCode(locale, context.req, context.res),
    geEnabled: flags.find((f) => f.name === GLOBAL_E_FLAG_NAME)?.value === true,
    bundleSlug,
    color: colorQuery,
    pack,
  });
  const seo = generateProductSeo(product, locale, {
    ...offer,
    url: buildOfferUrl({
      locale: context.locale,
      slug: product.slug,
      bundleSlug:
        offer.displayProduct.slug === product.slug ? null : bundleSlug,
      color: colorQuery,
      pack,
    }),
  });

  // FAQ JSON-LD 추가 (다국어 지원)
  if (seo) {
    // 화면과 **같은 함수 · 같은 입력** — 화면이 그리는 제품(`offer.displayProduct`)으로 고른다. 주소의 `product` 를 넘기면
    // camo·세트 주소에서 화면에 없는 FAQ 가 JSON-LD 에 남는다(#6094 · #6171 R1).
    // ⚠️ 알려진 한계: `resolveSeoOffer` 는 게임 키 번들을 못 고른다 — 게임 키 번들 업그레이드 주소에선 여전히 갈릴 수 있다.
    const faqTags = pdpFaqTagsForDisplayProduct(
      props.props.products,
      offer.displayProduct,
    );

    const faqItems = await getServerFaqItems(faqTags, context.locale);
    const faqJsonLd = generateFaqJsonLd(faqItems);
    if (faqJsonLd) {
      seo.jsonLd?.push(faqJsonLd);
    }
  }

  const color =
    typeof context.query.color === "string" ? context.query.color : null;

  return {
    ...props,
    props: {
      ...props.props,
      seo,
      color,
      // 보상판매 진입로 — /trade-in/ 과 같은 판정을 **서버 시계**로(#6101). 클라 시계면 시계를 바꾼 사람에게 미리 보인다.
      tradeInVisible: isTradeInPageVisible(isProd, new Date()),
    },
  };
}
